import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import {
  buildSceneCrossSectionElement,
  SceneCrossSections,
  type SceneCrossSectionElement,
} from '@scene'
import { useTheme } from '@mui/material'
import { useGuidToElementIncludingRips, useHideElements, useSceneElement } from '@editorHooks'
import { useSceneStore } from 'src/components/pages/Editor/stores/sceneStore'
import type { DefaultValues } from '..'

/**
 * Renders a live preview of the cross section currently configured in the multi
 * edit form. The edited positions are hidden in the planar model (via
 * `useHideElements`) and replaced by `SceneCrossSections`.
 *
 * Preview is gated on the shared form `crossSection` ("Ändern" unlock). Once
 * unlocked:
 * - UserDefined: all elements use the shared form CS (live while editing)
 * - AssemblyStandard: each element uses its own `elements[i].crossSection`
 *   (seeded on Ändern / updated from calc-checks `cross_section`)
 */
const SyncCrossSectionWithScene = () => {
  const theme = useTheme()
  const crossSection = useWatch<DefaultValues>({ name: 'crossSection' }) as
    | CrossSectionTimberOrCLTOrSteel
    | undefined
  const formPolicy = useWatch<DefaultValues>({ name: 'crossSectionPolicy' }) as
    | CrossSectionPolicy
    | undefined
  const elements = useWatch<DefaultValues>({ name: 'elements' }) as
    | DefaultValues['elements']
    | undefined
  const guidToElement = useGuidToElementIncludingRips()
  const guidToColor = useSceneStore(state => state.guidToColor)

  const elementGuids = useMemo(() => elements?.map(element => element.guid) ?? [], [elements])

  const sceneElements = useMemo<SceneCrossSectionElement[]>(() => {
    // No shared form CS means editing is still gated behind "Ändern" — keep the
    // real planar-model geometry visible instead of a shared preview.
    if (!crossSection || !elements?.length) return []

    const isAssemblyStandard = formPolicy === 'AssemblyStandard'

    return elements
      .map(element => {
        const previewCs = (
          isAssemblyStandard ? element.crossSection ?? crossSection : crossSection
        ) as CrossSectionTimberOrCLTOrSteel
        return buildSceneCrossSectionElement(
          {
            crossSection: previewCs,
            elementGuid: element.guid,
            color:
              (guidToColor[element.guid] as string) ?? (theme.scenePalette.selection as string),
            outlinesColor: theme.scenePalette.highlightDark as string,
          },
          guidToElement[element.guid],
        )
      })
      .filter((element): element is SceneCrossSectionElement => element !== null)
  }, [crossSection, elements, formPolicy, guidToElement, guidToColor, theme])

  useHideElements(crossSection ? elementGuids : undefined)

  const sceneElement = useMemo(
    () => ({
      enabled: !!crossSection && sceneElements.length > 0,
      properties: { elements: sceneElements },
      component: SceneCrossSections,
    }),
    [crossSection, sceneElements],
  )

  useSceneElement(sceneElement)

  return null
}

export default SyncCrossSectionWithScene
